/**
 * 만공차 체크 시뮬레이터.
 *
 * 제어(입력)는 **주차면(parkingLocations) 점유 on/off** 이고, 만공차등은 그 결과로
 * **관찰**하는 출력이다(직접 제어하지 않는다). CCTV–주차면 1:n 관계
 * (mapAssetFix.extractCctvParkingLinks, CCTV.linkedParkingLocationIds 기반)에 따라
 * 만공차등은 평소(공차 있음) ON(디밍 100)이고, 한 CCTV 가 담당하는 주차면이 모두
 * 점유되면(만차) 0 으로 꺼진다.
 *
 * 두 가지 모드로 동작한다:
 *  · dry-run OFF: 규칙을 **클라이언트에서 계산**해 맵 만공차등(초록 bloom)을 즉시 표시.
 *    빠른 관계/UI 확인용이며 백엔드를 거치지 않는다.
 *  · dry-run ON: 점유가 변해 선택 CCTV 의 만차 상태가 바뀌면 **조명제어서버까지** 실제
 *    만공차등 명령을 보낸다(LightsSimulatorService.triggerControl(type:'parking')).
 *    dry-run 캡처 모드라 하드웨어로는 안 나가고 fan-out 이 캡처되며, 캡처 이벤트가
 *    맵 만공차등 bloom 과 이벤트 패널을 구동한다. "점유 → 제어서버가 실제로 만공차등을
 *    요청하는가" 라는 소프트웨어 끝단을 검증한다.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  DeleteOutline as ClearIcon,
  PlaylistPlay as AutoRunIcon,
} from '@mui/icons-material';
import { useSimInteractionStore } from './model/simInteractionStore';
import { useLightsCaptureSession } from './hooks/useLightsCaptureSession';
import { LightsSimulatorService } from '../../services/LightsSimulatorService';
import { useStatusStore } from '../../map-editor-features/status/model/statusStore';
import type { CctvParkingLink } from './mapAssetFix';
import { sleep, type AutoCheckResult, type SimAutoRunHandle } from './autoTestUtils';
import AutoTestReportDialog from './AutoTestReportDialog';

interface FullVacancyCheckSimulatorProps {
  /** CCTV↔주차면 1:n 관계 (MapCanvasPanel 이 맵에서 추출). */
  cctvLinks: CctvParkingLink[];
}

/** 만공차등 디밍: 평소(공차 있음) 100, 담당 주차면이 모두 점유되면(만차) 0. */
const VACANT_DIM = 100;

const FullVacancyCheckSimulator = forwardRef<SimAutoRunHandle, FullVacancyCheckSimulatorProps>(({
  cctvLinks,
}, ref) => {
  // dry-run 캡처 세션(조명 체크 탭과 동일 훅): 토글 + getEvents 폴링 + 캡처 이벤트로
  // 맵 만공차등 bloom 구동.
  const {
    dryRunEnabled,
    togglingMode,
    modeError,
    toggleMode,
    events,
    clearEvents,
  } = useLightsCaptureSession();

  // 맵에서 CCTV 클릭 시 그 CCTV 로 포커스(비상벨 탭과 동일 채널).
  const selectedFromMap = useSimInteractionStore(s => s.selectedCctv);
  const setMapFocus = useSimInteractionStore(s => s.setMapFocus);
  const [cctvName, setCctvName] = useState<string>('');
  useEffect(() => {
    if (selectedFromMap?.name) setCctvName(selectedFromMap.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFromMap?.nonce]);

  // 선택된 CCTV 를 맵에서 하이라이트 + 센터링(모든 시뮬레이터 탭 공통 채널).
  // onInputChange 가 아니라 선택 "값"을 감시하는 effect 로 게시한다 — 탭 재진입,
  // 맵 클릭으로 채워진 선택까지 모두 맵에 반영. freeSolo 타이핑 잡음은 실제
  // 관계 목록과 일치할 때만 게시하는 것으로 막는다.
  useEffect(() => {
    if (cctvName && cctvLinks.some(l => l.cctvName === cctvName)) {
      setMapFocus({ kind: 'cctv', name: cctvName });
    }
  }, [cctvName, cctvLinks, setMapFocus]);

  // 점유 상태는 store 단일 소스(맵 클릭 ↔ 이 패널 공유). 맵은 빨간색으로 표시.
  const occupiedParkingIds = useSimInteractionStore(s => s.occupiedParkingIds);
  const toggleParkingOccupied = useSimInteractionStore(s => s.toggleParkingOccupied);
  const setParkingOccupied = useSimInteractionStore(s => s.setParkingOccupied);
  const occupied = useMemo(() => new Set(occupiedParkingIds), [occupiedParkingIds]);

  const cctvOptions = useMemo(() => cctvLinks.map(l => l.cctvName), [cctvLinks]);
  const link = useMemo(
    () => cctvLinks.find(l => l.cctvName === cctvName) || null,
    [cctvLinks, cctvName],
  );

  const occupiedCount = link
    ? link.parking.filter(p => occupied.has(p.id)).length
    : 0;
  const total = link ? link.parking.length : 0;
  const full = total > 0 && occupiedCount === total;
  const dimming = full ? 0 : VACANT_DIM;

  // 트리거 결과 표시(조명 서버 오류 / 캡처 0건 경고).
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [triggerWarn, setTriggerWarn] = useState<string | null>(null);

  // dry-run OFF: 클라이언트 계산으로 즉시 맵 만공차등 표시(서버 미경유).
  // 세션 훅이 이미 store 의 updateLightingStatus 를 구독하므로 여기선 getState 로 직접 호출.
  useEffect(() => {
    if (dryRunEnabled || !link) return;
    useStatusStore.getState().updateLightingStatus({
      cctvName: link.cctvName,
      section: '',
      lightType: '만공차등',
      on: !full,
      dimming,
      lastUpdate: Date.now(),
    });
  }, [dryRunEnabled, link, full, dimming]);

  // dry-run ON: 선택 CCTV 의 만차 상태가 바뀌면 조명제어서버에 실제 만공차등 명령 전송.
  // cctvName→마지막 전송값으로 중복 호출을 막는다(점유 칩 클릭마다 effect 재실행되므로).
  const lastSentRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!dryRunEnabled) {
      lastSentRef.current = {};
      return;
    }
    if (!link) return;
    const value = full ? 0 : VACANT_DIM;
    if (lastSentRef.current[link.cctvName] === value) return;
    lastSentRef.current[link.cctvName] = value;

    let cancelled = false;
    (async () => {
      setTriggerError(null);
      setTriggerWarn(null);
      try {
        const r = await LightsSimulatorService.triggerControl([link.cctvName], 'parking', value);
        if (cancelled) return;
        if (r.errors.length > 0) {
          setTriggerError(`조명 서버 오류: ${r.errors.join('; ')}`);
        } else if (r.captured === 0) {
          setTriggerWarn(
            `요청은 전송됐지만 캡처된 만공차등 0건입니다 — "${link.cctvName}" 에 연결된 만공차등 모듈이 없거나(매핑 미등록) 이미 목표값일 수 있습니다.`,
          );
        }
      } catch (e) {
        if (!cancelled) {
          setTriggerError(e instanceof Error ? e.message : '만공차등 트리거 실패');
          // 실패한 값은 캐시에서 제거해 다음 변경 때 재시도.
          lastSentRef.current[link.cctvName] = -1;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dryRunEnabled, link, full]);

  const toggleSpot = (id: string) => toggleParkingOccupied(id);

  const setAll = (value: boolean) => {
    if (!link) return;
    setParkingOccupied(link.parking.map(p => p.id), value);
  };

  // ── 자동 실행: CCTV(관계)마다 맵을 포커스해 보여주며 주차면을 하나씩 채우고,
  // 담당 주차면이 모두 차면 만공차등 디밍이 100→0 으로 내려가는지 확인한다. ──
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoReport, setAutoReport] = useState<AutoCheckResult[] | null>(null);
  const [showReport, setShowReport] = useState(false);
  const stopRequestedRef = useRef(false);

  // 판정 루프만 순수 추출(로컬 report/showReport state는 안 건드림) — 개별
  // "자동 실행" 버튼과 통합 자동 테스트(SimulatorPage index.tsx)가 공유한다.
  const runCheckLoop = useCallback(async (): Promise<AutoCheckResult[]> => {
    if (!dryRunEnabled) await toggleMode();
    const results: AutoCheckResult[] = [];
    for (const l of cctvLinks) {
        if (stopRequestedRef.current) break;
        setCctvName(l.cctvName);
        setMapFocus({ kind: 'cctv', name: l.cctvName });
        await sleep(600);

        const ids = l.parking.map(p => p.id);
        setParkingOccupied(ids, false);
        await sleep(300);
        for (const id of ids) {
          setParkingOccupied([id], true);
          await sleep(200);
        }

        let passed = false;
        let detail: string;
        try {
          const r = await LightsSimulatorService.triggerControl([l.cctvName], 'parking', 0);
          if (r.errors.length > 0) {
            detail = `조명 서버 오류: ${r.errors.join('; ')}`;
          } else if (r.captured === 0) {
            detail = '캡처된 만공차등 명령 0건 — 연결된 만공차등 모듈이 없을 수 있습니다.';
          } else {
            passed = true;
            detail = `주차면 ${ids.length}칸 만차 → 만공차등 디밍 100→0 확인(캡처 ${r.captured}건)`;
          }
        } catch (e) {
          detail = e instanceof Error ? e.message : '만공차등 트리거 실패';
        }
        setParkingOccupied(ids, false); // 다음 CCTV 를 위해 점유 해제

        results.push({ name: l.cctvName, passed, detail });
        setAutoReport([...results]);
    }
    return results;
  }, [dryRunEnabled, toggleMode, cctvLinks, setParkingOccupied, setMapFocus]);

  const handleAutoRun = async () => {
    if (cctvLinks.length === 0 || autoRunning) return;
    setAutoRunning(true);
    setAutoReport(null);
    stopRequestedRef.current = false;
    try {
      await runCheckLoop();
      setShowReport(true);
    } finally {
      setAutoRunning(false);
    }
  };

  const handleStopAutoRun = () => {
    stopRequestedRef.current = true;
  };

  useImperativeHandle(ref, () => ({
    runAutoCheck: async () => {
      if (autoRunning) return [];
      setAutoRunning(true);
      setAutoReport(null);
      stopRequestedRef.current = false;
      try {
        const results = await runCheckLoop();
        setAutoReport(results);
        return results;
      } finally {
        setAutoRunning(false);
      }
    },
    stopAutoCheck: handleStopAutoRun,
  }), [runCheckLoop, autoRunning]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        주차면 점유를 켜고 끄면(입력), 만공차등은 평소 ON(디밍 100)이다가 그 CCTV 가
        담당하는 주차면이 모두 차는 순간 0 으로 꺼집니다(출력). Dry-run 을 켜면 점유 변화가 조명제어서버까지
        가서 실제 만공차등 명령을 보내고(하드웨어 미전송·캡처만), 그 결과를 맵·아래 패널로
        확인합니다.
      </Typography>

      {/* dry-run 캡처 토글 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            Dry-run 캡처 모드
          </Typography>
          <Chip
            size="small"
            label={dryRunEnabled ? '활성' : '비활성'}
            color={dryRunEnabled ? 'success' : 'default'}
            sx={{ flexShrink: 0 }}
          />
          <Button
            variant={dryRunEnabled ? 'outlined' : 'contained'}
            color={dryRunEnabled ? 'error' : 'primary'}
            size="small"
            startIcon={dryRunEnabled ? <StopIcon /> : <PlayIcon />}
            onClick={() => void toggleMode()}
            disabled={togglingMode}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {togglingMode ? '전환 중...' : dryRunEnabled ? 'OFF' : 'ON'}
          </Button>
        </Stack>
        {modeError && (
          <Alert
            severity={modeError.includes('허용') || modeError.includes('403') ? 'warning' : 'error'}
            sx={{ mt: 1 }}
          >
            {modeError}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {dryRunEnabled
            ? '점유가 바뀌어 만차/해제되면 조명제어서버로 만공차등 명령을 보냅니다. 하드웨어로는 전송되지 않습니다.'
            : 'OFF 에서는 클라이언트 계산으로 맵 만공차등만 표시합니다(서버 미경유).'}
        </Typography>
      </Paper>

      {/* CCTV 선택 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2">
            CCTV 선택 {cctvOptions.length > 0 ? `(관계 ${cctvOptions.length})` : ''}
          </Typography>
          {autoRunning ? (
            <Button size="small" variant="outlined" color="error" onClick={handleStopAutoRun}>
              정지
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutoRunIcon />}
              onClick={handleAutoRun}
              disabled={cctvLinks.length === 0}
            >
              자동 실행
            </Button>
          )}
        </Stack>
        <Autocomplete
          freeSolo
          size="small"
          fullWidth
          options={cctvOptions}
          inputValue={cctvName}
          onInputChange={(_, v) => setCctvName(v)}
          renderInput={params => (
            <TextField
              {...params}
              label="CCTV"
              placeholder="P1_B1_2_22"
              helperText={
                cctvOptions.length > 0
                  ? '맵에서 CCTV 를 클릭하거나 여기서 선택'
                  : '이 맵에는 CCTV–주차면 관계가 없습니다'
              }
            />
          )}
        />
      </Paper>

      {triggerError && <Alert severity="error">{triggerError}</Alert>}
      {triggerWarn && <Alert severity="warning">{triggerWarn}</Alert>}

      {/* 담당 주차면 점유 제어 + 만공차등 출력 */}
      {link ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
          >
            <Typography variant="subtitle2">
              담당 주차면 {occupiedCount}/{total} 점유
            </Typography>
            <Chip
              size="small"
              color={full ? 'default' : 'success'}
              label={`만공차등 ${full ? '만차 · 디밍 0' : `ON · 디밍 ${dimming}`}`}
            />
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Button size="small" variant="contained" onClick={() => setAll(true)}>
              전체 점유
            </Button>
            <Button size="small" variant="outlined" onClick={() => setAll(false)}>
              전체 해제
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              maxHeight: 240,
              overflow: 'auto',
            }}
          >
            {link.parking.map(p => {
              const on = occupied.has(p.id);
              return (
                <Chip
                  key={p.id}
                  size="small"
                  label={p.name}
                  color={on ? 'warning' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  onClick={() => toggleSpot(p.id)}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>
        </Paper>
      ) : (
        <Alert severity="info" sx={{ py: 0.5 }}>
          CCTV 를 선택하면 담당 주차면을 점유/해제하며 만공차등 ON/OFF 를 확인할 수
          있습니다.
        </Alert>
      )}

      {/* dry-run 캡처 이벤트(조명제어서버 fan-out) */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
            만공차등 Fan-out 이벤트
            {dryRunEnabled && (
              <Chip size="small" label="폴링 중" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<ClearIcon />}
            onClick={() => clearEvents()}
            disabled={events.length === 0}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            초기화
          </Button>
        </Stack>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {dryRunEnabled
              ? '점유를 만차/해제로 바꾸면 캡처된 만공차등 명령이 여기 표시됩니다.'
              : 'Dry-run 을 ON 하고 주차면을 만차/해제로 바꾸세요.'}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ maxHeight: 220, overflow: 'auto' }}>
            {events.map(ev => (
              <Box
                key={ev.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.25 }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ width: 72, flexShrink: 0 }}>
                  {ev.at}
                </Typography>
                <Chip size="small" label={ev.cctvName} color="primary" variant="outlined" sx={{ maxWidth: 160 }} />
                <Chip size="small" label={ev.lightType} color="default" variant="outlined" />
                <Chip size="small" label={ev.on ? `${ev.value}%` : 'OFF'} color={ev.on ? 'success' : 'default'} />
                <Typography variant="caption" color="text.secondary">
                  {ev.mode}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Divider />
      <Typography variant="caption" color="text.secondary">
        주차면 칩(또는 맵의 주차면)을 클릭해 점유를 토글합니다. 담당 주차면이 모두
        점유되면 만공차등이 0 으로 꺼지고(해제 시 다시 100), dry-run 이 켜져 있으면 그
        명령이 조명제어서버까지 전송돼 위 패널에 캡처됩니다. "자동 실행"은 관계에 등록된
        CCTV 를 하나씩 맵에서 보여주며 주차면을 채우고 결과를 리포트로 보여줍니다.
      </Typography>

      <AutoTestReportDialog
        open={showReport}
        title="만공차 체크 — 자동 실행 리포트"
        results={autoReport ?? []}
        onClose={() => setShowReport(false)}
      />
    </Stack>
  );
});

FullVacancyCheckSimulator.displayName = 'FullVacancyCheckSimulator';

export default FullVacancyCheckSimulator;
